import { Dropdown, Input, Button, Menu, ConfigProvider } from "antd";
import { useTopic } from "./hooks/use-topics";
import { useEffect, useMemo, useState } from "react";
import { apiRequest, useModus } from "@vojomo-core/shared";
import { DownOutlined, SearchOutlined } from "@ant-design/icons";
import "./DocTypeSelect.css";
import { useNavigate } from "react-router-dom";
import { DeleteModal } from "../Modus/DocsList/DeleteModal";

interface DocType {
  task_name: string;
  question_agent: string;
  writing_agent: string;
}

export const TopicConfiguration = ({
  disabled = false
}: {
  disabled?: boolean;
}) => {
  const {
    topic,
    topicId,

    updateDocType,
    topicTypeOpen,
    setTopicTypeOpen,

    setIsTopicTypeModalVisible,

    topicType,
    topicTitle,

    setChangedDocType,

    output
  } = useTopic();

  const { resetChatContext } = useModus();

  const navigate = useNavigate();

  const [docTypes, setDocTypes] = useState<DocType[]>([]);
  const [searchText, setSearchText] = useState("");
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleDelete = async () => {
    setLoading(true);
    await apiRequest({
      subRoute: "topic",
      method: "POST",
      path: `/delete_topic`,
      body: {
        topicId: topic?._id
      }
    });
    setLoading(false);
    setIsDeleteModalVisible(false);
    navigate("/documents/docs");
  };
  // Get document types from API
  const getDocTypes = async () => {
    const docTypes = await apiRequest<DocType[]>({
      subRoute: "ai",
      path: `/get_writing_tasks?topic_id=${topicId}`,
      method: "GET"
    });

    setDocTypes(docTypes);
  };

  useEffect(() => {
    getDocTypes();
  }, [topicId]);

  const filteredDocTypes = useMemo(() => {
    if (!searchText) return docTypes;

    return docTypes.filter((dt) =>
      dt.task_name.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [searchText, docTypes]);

  // Generate menu items for dropdown
  const getMenuItems = () => {
    const menuItems: any[] = [];

    menuItems.push(
      <Menu.ItemGroup key="doc-types">
        {filteredDocTypes.map((dt) => (
          <Menu.Item
            key={dt.task_name}
            onClick={async () => {
              // Don't allow creating new doc if already the same type
              const isSameType =
                topicType === dt.task_name ||
                (topicType === "briefing-doc" &&
                  dt.task_name === "Alignment Report");

              if (isSameType) {
                setTopicTypeOpen(false);
                return;
              }

              if (!!topicType && !!output) {
                setChangedDocType(dt.task_name);
                setIsTopicTypeModalVisible(true);
              } else {
                await updateDocType(dt.task_name);
                // Reset chat context to show new starter text for the doc type
                await resetChatContext();
                setTopicTypeOpen(false);
              }
            }}
            className={
              topicType === dt.task_name ||
              (topicType === "briefing-doc" &&
                dt.task_name === "Alignment Report")
                ? "selected-item"
                : ""
            }
          >
            <div className="doc-item">{dt.task_name}</div>
          </Menu.Item>
        ))}
      </Menu.ItemGroup>
    );

    return menuItems;
  };

  const menu = (
    <Menu
      className="doc-type-menu"
      style={{ maxHeight: "calc(50vh - 20px)", overflow: "auto" }}
    >
      <div className="search-container">
        <Input
          prefix={<SearchOutlined />}
          placeholder="Search..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          className="search-input"
        />
      </div>
      {getMenuItems()}
    </Menu>
  );

  if (!topicType || topicType !== "building-block") {
    return (
      <ConfigProvider
        theme={{
          components: {
            Button: {
              defaultBorderColor: "rgb(44, 21, 97)",
              defaultHoverBorderColor: "rgb(64, 41, 117)", // 20 pxs lighter
              defaultBg: "rgb(44, 21, 97)",
              defaultHoverBg: "rgb(64, 41, 117)", // 20 pxs lighter
              defaultColor: "white"
            }
          }
        }}
      >
        <div className="flex items-center justify-between w-full">
          {isDeleteModalVisible && (
            <DeleteModal
              loading={loading}
              topicName={topic?.title || "Untitled Document"}
              topicType={topic?.type || ""}
              onCancel={() => setIsDeleteModalVisible(false)}
              onSubmit={handleDelete}
            />
          )}

          <Dropdown
            menu={{}}
            popupRender={() => <div className="doc-type-dropdown">{menu}</div>}
            open={topicTypeOpen}
            onOpenChange={setTopicTypeOpen}
            trigger={["click"]}
            disabled={disabled}
          >
            <Button className="my-5 p-0" type="text">
              <span id="doc-type" style={{ fontWeight: "700", color: "black" }}>
                {!topicType
                  ? "Select Doc Type"
                  : topicType === "building-block"
                    ? topicTitle
                    : topicType === "briefing-doc"
                      ? "Alignment Report"
                      : topicType}
              </span>
              {<DownOutlined />}
            </Button>
          </Dropdown>
        </div>
      </ConfigProvider>
    );
  }

  return <div className="my-5"></div>;
};
